import React, { useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { Check, ChevronLeft, ChevronRight, Loader2, Sparkles, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { SERVICE_CATEGORIES } from "@/data/services";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STEPS = ["step1", "step2", "step3", "step4", "step5", "step6"];

const QuoteEstimator = () => {
  const { t, i18n } = useTranslation();
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [data, setData] = useState({
    client_type: "",
    service_category: "",
    surface: "",
    dirtiness: "",
    urgency: "",
    city: "",
    postal_code: "",
    description: "",
    name: "",
    phone: "",
    email: "",
    rgpd: false,
  });

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const canNext = () => {
    if (step === 0) return !!data.client_type;
    if (step === 1) return !!data.service_category;
    if (step === 2) return !!data.surface;
    if (step === 3) return !!data.dirtiness;
    if (step === 4) return !!data.urgency && data.city.trim() && data.postal_code.trim();
    if (step === 5) return data.name && data.phone && data.email && data.rgpd;
    return false;
  };

  const next = () => setStep((s) => Math.min(s + 1, STEPS.length - 1));
  const prev = () => setStep((s) => Math.max(s - 1, 0));

  const submit = async () => {
    const errs = {};
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email))
      errs.email = t("quote.error_email");
    if (data.phone && !/^(0[1-9])[0-9]{8}$/.test(data.phone.replace(/\s/g, "")))
      errs.phone = t("quote.error_phone");
    if (Object.keys(errs).length > 0) { setFieldErrors(errs); return; }
    setFieldErrors({});
    setSubmitting(true);
    setErrorMsg("");
    try {
      const lang = i18n.language?.startsWith("en") ? "en" : "fr";
      const res = await axios.post(`${API}/quote`, { ...data, language: lang });
      setResult(res.data);
    } catch (e) {
      console.error(e);
      setErrorMsg(e?.response?.data?.detail || t("quote.toast_error"));
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setResult(null);
    setStep(0);
    setData({
      client_type: "",
      service_category: "",
      surface: "",
      dirtiness: "",
      urgency: "",
      city: "",
      postal_code: "",
      description: "",
      name: "",
      phone: "",
      email: "",
      rgpd: false,
    });
  };

  const progress = ((step + 1) / STEPS.length) * 100;

  if (result) {
    return (
      <div className="rounded-3xl bg-white border border-slate-200 ring-soft p-8 sm:p-10" data-testid="quote-result">
        <div className="inline-flex items-center justify-center h-12 w-12 rounded-full bg-emerald-100">
          <Check className="h-6 w-6 text-brand-green" />
        </div>
        <h3 className="mt-4 font-heading font-extrabold text-2xl sm:text-3xl text-slate-900">
          {t("quote.result.title")}
        </h3>
        <p className="mt-2 text-slate-600">{t("quote.result.next")}</p>

        <div className="mt-6 rounded-2xl bg-gradient-to-br from-emerald-50 to-sky-50 p-6 border border-slate-200">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-brand-blue">
            {t("quote.result.range")}
          </div>
          <div className="mt-1 font-heading font-extrabold tracking-tight text-4xl sm:text-5xl text-slate-900">
            <span data-testid="quote-result-min">{result.estimate_min}</span> € -<span data-testid="quote-result-max">{result.estimate_max}</span> €
          </div>
          <p className="mt-3 text-sm text-slate-600 leading-relaxed" data-testid="quote-result-disclaimer">{t("quote.result.disclaimer")}</p>
        </div>

        <div className="mt-6 flex flex-col sm:flex-row gap-3">
          <Button onClick={reset} variant="outline" className="rounded-full" data-testid="quote-new-button">
            {t("quote.result.new")}
          </Button>
          <a href="tel:+33649502801" className="btn-primary">
            06 49 50 28 01
            <ArrowRight className="h-4 w-4" />
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-3xl bg-white border border-slate-200 ring-soft p-6 sm:p-8" data-testid="quote-estimator">
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-brand-blue">
          {t("quote.step")} {step + 1} {t("quote.of")} {STEPS.length}
        </div>
        <Sparkles className="h-4 w-4 text-brand-green" />
      </div>
      <Progress value={progress} className="h-1.5 mb-6" />

      <AnimatePresence mode="wait">
        <motion.div
          key={step}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          transition={{ duration: 0.25 }}
        >
          {step === 0 && (
            <div data-testid="quote-step-1">
              <h3 className="font-heading font-bold text-2xl text-slate-900 mb-5">{t("quote.step1.title")}</h3>
              <RadioGroup
                value={data.client_type}
                onValueChange={(v) => set("client_type", v)}
                className="grid grid-cols-1 sm:grid-cols-2 gap-3"
              >
                {Object.entries(t("quote.step1.options", { returnObjects: true })).map(([k, v]) => (
                  <Label
                    key={k}
                    htmlFor={`ct-${k}`}
                    className={`cursor-pointer rounded-xl border px-4 py-4 flex items-center gap-3 transition ${
                      data.client_type === k
                        ? "border-brand-green bg-emerald-50"
                        : "border-slate-200 hover:border-slate-300"
                    }`}
                    data-testid={`quote-client-type-${k}`}
                  >
                    <RadioGroupItem id={`ct-${k}`} value={k} />
                    <span className="font-medium text-slate-800">{v}</span>
                  </Label>
                ))}
              </RadioGroup>
            </div>
          )}

          {step === 1 && (
            <div data-testid="quote-step-2">
              <h3 className="font-heading font-bold text-2xl text-slate-900 mb-5">{t("quote.step2.title")}</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {SERVICE_CATEGORIES.map((c) => {
                  const Icon = c.icon;
                  const selected = data.service_category === c.slug;
                  return (
                    <button
                      key={c.slug}
                      type="button"
                      onClick={() => set("service_category", c.slug)}
                      className={`text-left rounded-xl border px-4 py-4 flex items-start gap-3 transition ${
                        selected ? "border-brand-green bg-emerald-50" : "border-slate-200 hover:border-slate-300"
                      }`}
                      data-testid={`quote-service-${c.slug}`}
                    >
                      <Icon className="h-5 w-5 text-brand-green mt-0.5" />
                      <div>
                        <div className="font-semibold text-slate-900 text-sm">{t(`categories.${c.slug}.name`)}</div>
                        <div className="text-xs text-slate-500 mt-0.5">{t(`categories.${c.slug}.tagline`)}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {step === 2 && (
            <div data-testid="quote-step-3">
              <h3 className="font-heading font-bold text-2xl text-slate-900 mb-5">{t("quote.step3.title")}</h3>
              <RadioGroup
                value={data.surface}
                onValueChange={(v) => set("surface", v)}
                className="grid grid-cols-1 sm:grid-cols-2 gap-3"
              >
                {Object.entries(t("quote.step3.options", { returnObjects: true })).map(([k, v]) => (
                  <Label
                    key={k}
                    htmlFor={`s-${k}`}
                    className={`cursor-pointer rounded-xl border px-4 py-4 flex items-center gap-3 transition ${
                      data.surface === k ? "border-brand-green bg-emerald-50" : "border-slate-200 hover:border-slate-300"
                    }`}
                    data-testid={`quote-surface-${k}`}
                  >
                    <RadioGroupItem id={`s-${k}`} value={k} />
                    <span className="font-medium text-slate-800">{v}</span>
                  </Label>
                ))}
              </RadioGroup>
            </div>
          )}

          {step === 3 && (
            <div data-testid="quote-step-4">
              <h3 className="font-heading font-bold text-2xl text-slate-900 mb-5">{t("quote.step4.title")}</h3>
              <RadioGroup
                value={data.dirtiness}
                onValueChange={(v) => set("dirtiness", v)}
                className="grid grid-cols-1 sm:grid-cols-2 gap-3"
              >
                {Object.entries(t("quote.step4.options", { returnObjects: true })).map(([k, v]) => (
                  <Label
                    key={k}
                    htmlFor={`d-${k}`}
                    className={`cursor-pointer rounded-xl border px-4 py-4 flex items-center gap-3 transition ${
                      data.dirtiness === k ? "border-brand-green bg-emerald-50" : "border-slate-200 hover:border-slate-300"
                    }`}
                    data-testid={`quote-dirtiness-${k}`}
                  >
                    <RadioGroupItem id={`d-${k}`} value={k} />
                    <span className="font-medium text-slate-800">{v}</span>
                  </Label>
                ))}
              </RadioGroup>
            </div>
          )}

          {step === 4 && (
            <div data-testid="quote-step-5">
              <h3 className="font-heading font-bold text-2xl text-slate-900 mb-5">{t("quote.step5.title")}</h3>
              <div className="space-y-4">
                <div>
                  <Label className="mb-2 block">{t("quote.step5.urgency_label")}</Label>
                  <Select value={data.urgency} onValueChange={(v) => set("urgency", v)}>
                    <SelectTrigger data-testid="quote-urgency-trigger">
                      <SelectValue placeholder="—" />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(t("quote.step5.options", { returnObjects: true })).map(([k, v]) => (
                        <SelectItem key={k} value={k} data-testid={`quote-urgency-${k}`}>
                          {v}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="city" className="mb-2 block">{t("quote.step5.city")}</Label>
                    <Input id="city" value={data.city} onChange={(e) => set("city", e.target.value)} data-testid="quote-city" />
                  </div>
                  <div>
                    <Label htmlFor="postal" className="mb-2 block">{t("quote.step5.postal")}</Label>
                    <Input id="postal" value={data.postal_code} onChange={(e) => set("postal_code", e.target.value)} data-testid="quote-postal-code" />
                  </div>
                </div>
              </div>
            </div>
          )}

          {step === 5 && (
            <div data-testid="quote-step-6">
              <h3 className="font-heading font-bold text-2xl text-slate-900 mb-5">{t("quote.step6.title")}</h3>
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name" className="mb-2 block">{t("quote.step6.name")}</Label>
                    <Input id="name" value={data.name} onChange={(e) => set("name", e.target.value)} data-testid="quote-name-input" />
                  </div>
                  <div>
                    <Label htmlFor="phone" className="mb-2 block">{t("quote.step6.phone")}</Label>
                    <Input id="phone" value={data.phone} onChange={(e) => { set("phone", e.target.value); setFieldErrors((fe) => ({ ...fe, phone: undefined })); }} data-testid="quote-phone" />
                    {fieldErrors.phone && <p className="text-xs text-red-500 mt-1">{fieldErrors.phone}</p>}
                  </div>
                </div>
                <div>
                  <Label htmlFor="email" className="mb-2 block">{t("quote.step6.email")}</Label>
                  <Input id="email" type="email" value={data.email} onChange={(e) => { set("email", e.target.value); setFieldErrors((fe) => ({ ...fe, email: undefined })); }} data-testid="quote-email-input" />
                  {fieldErrors.email && <p className="text-xs text-red-500 mt-1">{fieldErrors.email}</p>}
                </div>
                <div>
                  <Label htmlFor="desc" className="mb-2 block">{t("quote.step6.description")}</Label>
                  <Textarea id="desc" value={data.description} onChange={(e) => set("description", e.target.value)} data-testid="quote-description" />
                </div>
                <Label className="flex items-start gap-3 cursor-pointer text-sm text-slate-600">
                  <Checkbox checked={data.rgpd} onCheckedChange={(v) => set("rgpd", !!v)} data-testid="quote-rgpd" />
                  <span>{t("quote.step6.rgpd")}</span>
                </Label>
                {errorMsg && <div className="text-sm text-red-600">{errorMsg}</div>}
              </div>
            </div>
          )}
        </motion.div>
      </AnimatePresence>

      <div className="mt-8 flex items-center justify-between">
        <Button
          variant="ghost"
          onClick={prev}
          disabled={step === 0}
          className="rounded-full"
          data-testid="quote-prev-button"
        >
          <ChevronLeft className="h-4 w-4 mr-1" /> {t("common.cta_prev")}
        </Button>
        {step < STEPS.length - 1 ? (
          <Button
            onClick={next}
            disabled={!canNext()}
            className="rounded-full bg-brand-green hover:bg-brand-green/90"
            data-testid="quote-next-button"
          >
            {t("common.cta_next")} <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        ) : (
          <Button
            onClick={submit}
            disabled={!canNext() || submitting}
            className="rounded-full bg-brand-green hover:bg-brand-green/90"
            data-testid="quote-submit"
          >
            {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            {t("quote.step6.submit")}
          </Button>
        )}
      </div>
    </div>
  );
};

export default QuoteEstimator;
